import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import keyCodes from 'utils/keyCodes';
import { noop } from 'utils/func';
import { isInViewport } from 'utils/isInViewport';

import Button from 'widgets/Button';
import Icon from 'widgets/Icon/Icon';
import Tooltip from 'widgets/Tooltip/Tooltip';

export default class QuantityControls extends PureComponent {
  static propTypes = {
    beforeChange: PropTypes.func,
    className: PropTypes.string,
    classNamePrefix: PropTypes.string,
    disabled: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    isUpdating: PropTypes.bool,
    maxQuantity: PropTypes.number,
    minQuantity: PropTypes.number,
    onWarning: PropTypes.func,
    quantity: PropTypes.number.isRequired,
    warning: PropTypes.string,
    addButtonLabel: PropTypes.string,
    addButtonType: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'buy']),
    buttonsSize: PropTypes.oneOf(['small', 'large']),
    showClearButton: PropTypes.bool,
    wrapperRef: PropTypes.object,
    clearWarning: PropTypes.func,
    tooltipForcePosition: PropTypes.array,
  };

  static defaultProps = {
    minQuantity: 0,
    maxQuantity: 99,
    onWarning: noop,
    beforeChange: () => true,
    classNamePrefix: 'quantityControls',
    addButtonLabel: 'Add',
    addButtonType: 'buy',
    showClearButton: false,
    wrapperRef: React.createRef(),
    clearWarning: noop,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextQuantity = nextProps.quantity;

    if (nextQuantity !== prevState.quantityProps) {
      return {
        quantity: nextQuantity,
        quantityProps: nextQuantity,
      };
    }

    return null;
  }

  state = {};

  componentDidUpdate(prevProps) {
    // track analytics when max quantity warning has just been displayed
    if (this.relevantWarningExists(this.props) && !this.relevantWarningExists(prevProps)) {
      this.props.onWarning();
    }
    // React does not see the difference between 00002 and 2, therefore we need to update the value ourselves
    if (this.quantityInput) {
      const { quantity } = this.props;
      this.quantityInput.value = quantity && quantity.length ? parseInt(quantity, 10) : quantity;
    }
  }

  onQuantitySubmit = (quantityDelta) => {
    this.props.onUpdate(quantityDelta);
  };

  relevantWarningExists = (props) => props.warning && !!props.warning.length;

  changeQuantity = (quantityDelta) => {
    if (!this.props.beforeChange()) {
      return;
    }

    this.onQuantitySubmit(quantityDelta);
  };

  increase = (event) => {
    event.preventDefault();
    this.changeQuantity(1);
  };

  decrease = (event) => {
    event.preventDefault();
    this.changeQuantity(-1);
  };

  reset = (event) => {
    event.preventDefault();
    this.changeQuantity(-this.state.quantity);
  };

  onChange = (event) => {
    const { value: nextQuantity } = event.target;

    if (this.isQuantityInRange(nextQuantity)) {
      this.setState({
        quantity: nextQuantity,
      });
    }
  };

  onFocus = (event) => {
    const quantityInput = event.target;
    try {
      quantityInput.setSelectionRange(0, 3);
    } catch (error) {
      quantityInput.select();
    }
  };

  bindInput = (input) => {
    this.quantityInput = input;
  };

  handleKeyPress = (event) => {
    const { key, keyCode } = event;

    if (key === 'Enter' || keyCode === keyCodes.ENTER) {
      this.quantityInput.blur();
    }

    if (!this.isDigitPressed(event)) {
      event.preventDefault();
    }
  };

  isDigitPressed = (event) => {
    const { key, keyCode } = event;
    return /\d/.test(key) || (keyCode >= keyCodes[0] && keyCode <= keyCodes[9]);
  };

  isQuantityInRange = (quantity) => {
    const { minQuantity, maxQuantity } = this.props;
    return minQuantity <= quantity && quantity <= maxQuantity;
  };

  handleQuantityInputBlur = () => {
    const { quantity } = this.props;
    const { quantity: nextQuantity } = this.state;

    const quantityDelta = nextQuantity - quantity;

    if (quantityDelta && this.isQuantityInRange(nextQuantity)) {
      this.onQuantitySubmit(quantityDelta);
    }
  };

  renderAddButton = () => {
    const {
      warning,
      classNamePrefix,
      disabled,
      wrapperRef,
      addButtonType,
      addButtonLabel,
      buttonsSize,
      clearWarning,
      tooltipForcePosition,
    } = this.props;

    const isVisible = isInViewport(wrapperRef.current);

    const addButton = (
      <Button
        disabled={disabled}
        {...{ [addButtonType]: true }}
        size={buttonsSize}
        className={`${classNamePrefix}__add`}
        onClick={this.increase}
      >
        {addButtonLabel}
      </Button>
    );

    return (isVisible && this.relevantWarningExists(this.props))
      ? (
        <Tooltip
          forceShow
          defaultPositions={tooltipForcePosition || ['below', 'center']}
          label={addButton}
          className={`${classNamePrefix}__addButtonWarning`}
          onClose={clearWarning}
          detectCollisions={!tooltipForcePosition}
        >
          {warning}
        </Tooltip>)
      : addButton;
  };

  renderControls = () => {
    const {
      disabled,
      isUpdating,
      minQuantity,
      maxQuantity,
      warning,
      classNamePrefix,
      addButtonType,
      buttonsSize,
      wrapperRef,
      clearWarning,
      tooltipForcePosition,
    } = this.props;

    const { quantity } = this.state;

    const isVisible = isInViewport(wrapperRef.current);

    const input = (
      <input
        type="number"
        className={classNames(
          'gn-input__field',
          `${classNamePrefix}__changeQuantityInput`,
          `gn-input__field--${buttonsSize}`,
        )}
        disabled={disabled || isUpdating}
        min={minQuantity}
        max={maxQuantity}
        name="quantity"
        onBlur={this.handleQuantityInputBlur}
        onChange={this.onChange}
        onClick={this.onFocus}
        onKeyPress={this.handleKeyPress}
        value={quantity}
        ref={this.bindInput}
        aria-label="Quantity"
      />);
    return (
      <>
        <Button
          secondary
          size={buttonsSize}
          className={`${classNamePrefix}__changeQuantity--minus`}
          disabled={disabled || isUpdating}
          onClick={this.decrease}
        >
          <Icon name="minus" className="gn-button__icon" />
        </Button>

        {(isVisible && this.relevantWarningExists(this.props))
          ? (
            <Tooltip
              forceShow
              defaultPositions={tooltipForcePosition || ['below', 'center']}
              label={input}
              className={`${classNamePrefix}__warning`}
              onClose={clearWarning}
              detectCollisions={!tooltipForcePosition}
            >
              {warning}
            </Tooltip>)
          : input}

        <Button
          {...{
            [addButtonType]: true,
          }}
          size={buttonsSize}
          className={`${classNamePrefix}__changeQuantity--plus`}
          disabled={disabled || isUpdating}
          onClick={this.increase}
        >
          <Icon name="plus" className="gn-button__icon" />
        </Button>
        {this.props.showClearButton && (
          <Button
            secondary
            size={buttonsSize}
            className={`${classNamePrefix}__changeQuantity--reset`}
            disabled={isUpdating}
            onClick={this.reset}
          >
            <Icon name="close" className="gn-button__icon" />
          </Button>
        )}
      </>
    );
  };

  render() {
    const {
      quantity: propsQuantity,
      className,
      classNamePrefix,
      wrapperRef,
    } = this.props;
    return (
      <div
        className={classNames(`${classNamePrefix}__wrapper`, className)}
        ref={wrapperRef}
      >
        {propsQuantity
          ? this.renderControls()
          : this.renderAddButton()
        }
      </div>
    );
  }
}
